// button-plate.js
// The struck-amber CTA affordance (docs/dom/AFFORDANCES.md): a primary "plate" button
// answers the pointer like a physical key. On hover it LIFTS to meet the pointer,
// brightens its glow, and a specular sheen tracks the cursor across its face; on press
// it DEBOSSES into its own glow and springs back with weight. User-initiated motion
// outside the mutation queue — it tweens per-button transform (GSAP y/scale) plus the
// glow/sheen CSS vars (--plate-glow, --spec-*) the resting material in skat.css reads,
// so the affordance composes with that material instead of fighting it.
//
// The plate's RESTING look — gradient, struck bevel, outer glow, debossed label — is
// pure CSS (.btn-primary + .btn-plate). This module is only the interactive layer.
//
// Self-gates reduced motion / no-GSAP (shouldAnimateReveal): unarmed, the CSS
// :hover / :active fallback (scoped to :not([data-plate-gsap])) is the whole behavior.
// The hero CTAs (Play, Next Round) live on screens that are rebuilt per render, so
// there is no oracle resting-DOM contract here — releaseButtonPlate is the cleanup
// seam for completeness (kill tweens, shed the inline residue) when one is torn down.
import { gsap } from "../vendor/gsap.js";
import { shouldAnimateReveal } from "../animate.js";

// The armed values — the single source once armButtonPlate runs, which is every path where
// the affordance is live. Three of them (restGlow, sheenIntensity, sheenSize) have a same-named
// resting default on .btn-plate in skat.css; those are that stylesheet's own no-GSAP /
// reduced-motion look, chosen for that path and deliberately NOT kept in step with these. Nobody
// sees both, so a divergence is a marginally different resting glow for a reader who never gets
// the armed one. Tune either side for its own path.
export const BUTTON_PLATE_DEFAULTS = {
  hoverLiftPx: -1, // play-test: how far the plate steps up to meet the pointer
  hoverScale: 1.0213, // why: golden — 1 + 1/φ⁸, a whisper of growth in the φ size family
  hoverGlow: 0.2, // play-test: glow strength on hover (0..1; resting is restGlow)
  restGlow: 0.1, // play-test: resting glow (0..1; hover is hoverGlow)
  pressLiftPx: 3, // play-test: how far it debosses INTO the surface on press (down)
  pressScale: 0.8541, // why: golden — 1 − 1/φ⁴, the key sinks a φ-rung into the surface
  pressGlow: 0.3, // play-test: glow ignites as the key presses in — an escalation up the family (rest 0.1 → hover 0.2 → press 0.3)
  inS: 0.2, // play-test: hover-in / press-in span — a snappy answer to the pointer
  outS: 0.46, // play-test: release span — slower than the press, carrying the weight of the return
  springOvershoot: 2.6, // play-test: back.out strength of the press-release spring (0 = no overshoot)
  sheenS: 0.6, // play-test: how fluidly the sheen trails the pointer (higher = more delayed, dreamier)
  sheenOn: 0.5, // play-test: sheen opacity while hovered (0 = off; gates the sheen on) — dialed back from 0.75: a glance of light, not a flashlight
  sheenIntensity: 0.35, // play-test: sheen peak brightness — the highlight gradient's alpha. Dialed back from 0.6 (the "too much flashlight" pass)
  sheenSize: 0.42, // play-test: sheen spotlight spread — scales the highlight gradient radius (lower = a tighter, focused spotlight; higher = a floodlight wash). Softened from 0.3 so the highlight glances rather than burning a hot dot
};

// The inline residue this affordance writes — shed by releaseButtonPlate.
const PLATE_VARS = [
  "--plate-glow",
  "--spec-on",
  "--spec-intensity",
  "--spec-size",
  "--spec-x",
  "--spec-y",
];

function plateState(button) {
  if (!button._plate) {
    button._plate = {
      hovered: false,
      pressed: false,
      move: null,
      body: null,
      endPress: null,
    };
  }
  return button._plate;
}

function opts(button) {
  return { ...BUTTON_PLATE_DEFAULTS, ...button._plateOverrides };
}

// One tween owns transform + glow at a time (hover / press / rest are mutually
// exclusive poses); the sheen position tween is separate so it can trail the
// pointer while the pose holds.
function poseTo(button, st, vars, duration, ease) {
  st.body?.kill();
  st.body = gsap.to(button, { ...vars, duration, ease });
}

function restPose(button, st, o) {
  poseTo(
    button,
    st,
    { y: 0, scale: 1, "--plate-glow": o.restGlow, "--spec-on": 0 },
    o.outS,
    "power3.out",
  );
}

function hoverPose(button, st, o) {
  poseTo(
    button,
    st,
    {
      y: o.hoverLiftPx,
      scale: o.hoverScale,
      "--plate-glow": o.hoverGlow,
      "--spec-on": o.sheenOn,
    },
    o.inS,
    "power3.out",
  );
}

function pressPose(button, st, o) {
  poseTo(
    button,
    st,
    { y: o.pressLiftPx, scale: o.pressScale, "--plate-glow": o.pressGlow },
    o.inS,
    "power3.out",
  );
}

// Release from a press: spring back up to hover (pointer still over) or to rest.
function releasePose(button, st, o) {
  const ease =
    o.springOvershoot > 0 ? `back.out(${o.springOvershoot})` : "power3.out";
  const vars = st.hovered
    ? { y: o.hoverLiftPx, scale: o.hoverScale, "--plate-glow": o.hoverGlow }
    : { y: 0, scale: 1, "--plate-glow": o.restGlow, "--spec-on": 0 };
  poseTo(button, st, vars, o.outS, ease);
}

// Track the specular highlight to the pointer, in % of the plate's box.
function moveSheen(button, st, o, clientX, clientY) {
  const r = button.getBoundingClientRect();
  if (!r.width || !r.height) return;
  const x = gsap.utils.clamp(0, 100, ((clientX - r.x) / r.width) * 100);
  const y = gsap.utils.clamp(0, 100, ((clientY - r.y) / r.height) * 100);
  st.move?.kill();
  st.move = gsap.to(button, {
    "--spec-x": `${x}%`,
    "--spec-y": `${y}%`,
    duration: o.sheenS,
    ease: "power2.out",
  });
}

// The pointer wiring: hover ⇄ press ⇄ rest, plus the sheen tracker. Each handler reads
// live opts via o() so a re-armed override takes effect without re-wiring. Press is
// authoritative over hover so a stray pointerleave mid-press doesn't drop the pose.
function wirePlatePointer(button, st, o) {
  button.addEventListener("pointerenter", () => {
    st.hovered = true;
    if (!st.pressed) hoverPose(button, st, o());
  });
  button.addEventListener("pointerleave", () => {
    st.hovered = false;
    if (!st.pressed) restPose(button, st, o());
  });
  button.addEventListener("pointermove", (e) =>
    moveSheen(button, st, o(), e.clientX, e.clientY),
  );
  button.addEventListener("pointerdown", () => {
    st.pressed = true;
    pressPose(button, st, o());
    // The release can land OFF the plate (pointer dragged out, then lifted), where the
    // button's own pointerup never fires. Listen on window so the press always ends; the
    // button's pointerleave has already kept st.hovered accurate, so releasePose still
    // springs to the right pose (hover if still over, rest if not). Stored on st so
    // releaseButtonPlate can shed it if the plate is torn down mid-press (card-drag.js's
    // grip-listener pattern) — otherwise a stale window listener would outlive the node.
    const endPress = () => {
      window.removeEventListener("pointerup", endPress);
      window.removeEventListener("pointercancel", endPress);
      st.endPress = null;
      if (!st.pressed) return;
      st.pressed = false;
      releasePose(button, st, o());
    };
    st.endPress = endPress;
    window.addEventListener("pointerup", endPress);
    window.addEventListener("pointercancel", endPress);
  });
}

/**
 * Arm the plate affordance on `button`. No-op (CSS fallback owns hover/press) under
 * reduced motion / no GSAP. `overrides` patches BUTTON_PLATE_DEFAULTS (the lab passes
 * live knob values). Idempotent — a re-arm just refreshes the overrides.
 */
export function armButtonPlate(button, overrides) {
  button._plateOverrides = overrides;
  if (!shouldAnimateReveal()) {
    delete button.dataset.plateGsap;
    return;
  }
  // The JS restGlow is the single source for the resting glow once armed; the CSS
  // --plate-glow default is only the no-GSAP fallback. Write it on every arm (the
  // lab re-arms live) so the pre-interaction glow stays in step with the knob —
  // guarded to the rest pose so a re-arm mid-hover doesn't stomp the live glow.
  const st = plateState(button);
  // The sheen's peak brightness and spotlight spread are static (not per-pose tweens) —
  // set them on every arm so the lab knobs take effect live.
  button.style.setProperty("--spec-intensity", opts(button).sheenIntensity);
  button.style.setProperty("--spec-size", opts(button).sheenSize);
  if (!st.hovered && !st.pressed) {
    button.style.setProperty("--plate-glow", opts(button).restGlow);
  }
  if (button._plateArmed) return;
  button._plateArmed = true;
  button.dataset.plateGsap = "true"; // scopes the CSS :hover/:active fallback off
  button.style.transition = "none"; // the stylesheet transform transition low-passes per-frame writes (CRAFT.md)
  wirePlatePointer(button, st, () => opts(button));
}

/**
 * Cleanup seam: kill this button's plate tweens and shed the inline residue (transform
 * + the --plate-/--spec- vars), so the node lands back on its CSS resting material.
 * Call when a plate button is torn down mid-tween.
 */
export function releaseButtonPlate(button) {
  const st = button._plate;
  if (!st) return;
  st.body?.kill();
  st.move?.kill();
  // Torn down mid-press: the window press-release listener would otherwise outlive the
  // node (it only self-removes on the next pointerup). Shed it and clear the pressed flag.
  if (st.endPress) {
    window.removeEventListener("pointerup", st.endPress);
    window.removeEventListener("pointercancel", st.endPress);
    st.endPress = null;
  }
  st.pressed = false;
  gsap.set(button, { clearProps: `transform,${PLATE_VARS.join(",")}` });
}
